import { Footer } from "@/components/LandingPage/Footer";
import { GetStarted } from "@/components/LandingPage/GetStarted";
import Navbar from "@/components/LandingPage/Navbar";
import {
  Box,
  Button,
  Center,
  Container,
  Grid,
  Heading,
  Spacer,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { GetStaticProps } from "next";
import { useTranslations } from "next-intl";
import Head from "next/head";
import Image from "next/image";
import happyWoman from "@root/public/images/photos/happy-woman.png";
import coachWithTeam from "@root/public/images/photos/coach-with-team.png";
import enterprise from "@root/public/images/photos/enterprise.png";
import type { Metadata } from "next";
import { TestimonialsSection } from "@/components/LandingPage/TestimonialsSection";
import heroBg from "@root/public/images/photos/clarifayo_community.jpg";
import { DashDivider } from "@/components/LandingPage/DashDivider";
import coachDashboardImage from "@root/public/images/screenshots/coach_dashboard.png";
import salesPipelineImage from "@root/public/images/screenshots/sales_pipeline.png";
import financialPlannerImage from "@root/public/images/screenshots/financial_planner.png";
import teamDashboardImage from "@root/public/images/screenshots/team_dashboard.png";
import coachWithTeamDashboardImage from "@root/public/images/screenshots/coach_with_team_dashboard.png";
import teamPerformanceImage from "@root/public/images/screenshots/team_performance.png";
import customFormsImage from "@root/public/images/screenshots/custom_forms.png";

import { BirdNestIcon } from "@/components/LandingPage/BirdNestIcon";
import Link from "next/link";
import { RoleToggler } from "@/components/LandingPage/RoleToggler";
import { BulletPoint } from "@/components/LandingPage/BulletPoint";
import { useRouter } from "next/router";
import { useEffect } from "react";

export const metadata: Metadata = {
  title: "Clarifayo",
  description: "Unlock your potential with the ultimate coaching experience!",
};

const Hero = () => {
  const t = useTranslations("AboutUs");

  return (
    <Stack
      gap={{ base: 8, md: 14 }}
      py={{ base: 8, md: 20 }}
      px={{
        base: 8,
        md: 20,
      }}
      justifyContent={"flex-end"}
      bgColor="brand.1000"
      h={{
        base: "90vh",
        md: "90vh",
      }}
      position="relative"
      overflow={"hidden"}
      backgroundImage={`url(${heroBg.src})`}
      backgroundSize="cover"
      backgroundPosition="center"
      backgroundAttachment="fixed"
    >
      <Box className="hero-bg"></Box>
      <Box position="absolute" top={0} left={0} w="full">
        <Navbar />
      </Box>

      <Stack
        direction={["column", "row"]}
        gap={16}
        alignItems={"flex-end"}
        w="full"
        zIndex={1}
      >
        <Box flex={1} maxW={{ base: "full", md: "60vw" }}>
          <Heading
            fontWeight={600}
            fontSize={{ base: "42px", md: "68px" }}
            mb={4}
            textAlign={{ base: "center", md: "left" }}
            color="white"
            lineHeight={"1.1"}
            letterSpacing={".1rem"}
            fontFamily={`'Marlide', sans-serif`}
          >
            <Text>{t("title")}</Text>
          </Heading>
          <Text
            fontSize={{ base: "18px", md: "22px" }}
            color="white"
            textAlign={{ base: "center", md: "left" }}
            maxW={{ base: "full", md: "500px" }}
            mb={8}
          >
            {t("subtitle")}
          </Text>
        </Box>
      </Stack>
    </Stack>
  );
};

const SectionHeader = ({
  title,
  description,
}: {
  title: string;
  description: string;
}) => {
  return (
    <Box
      maxW={{
        base: "full",
        md: "50%",
      }}
      mb={16}
    >
      <Heading
        fontSize={"42px"}
        mb={8}
        fontFamily={"Marlide"}
        textTransform={"uppercase"}
        letterSpacing={".1rem"}
      >
        {title}
      </Heading>
      <Text
        fontSize={"lg"}
        dangerouslySetInnerHTML={{ __html: description }}
      ></Text>
    </Box>
  );
};

const CoachSection = () => {
  const t = useTranslations();
  return (
    <Container
      maxW="container.xl"
      py={{ base: 8, md: 16 }}
      px={{ base: 8, md: 24 }}
      id="coach"
    >
      <SectionHeader
        title={t("roles.coach")}
        description={t("ToWhom.coachSection.description")}
      />
      <VStack
        w="full"
        gap={{
          base: 8,
          md: 16,
        }}
      >
        <Heading fontSize={"3xl"} fontWeight={600}>
          {t("ToWhom.benefits")}
        </Heading>
        <DashDivider />
        <Grid
          w="full"
          gridTemplateColumns={{
            base: "repeat(1, 1fr)",
            md: "repeat(2, 1fr)",
          }}
          gap={16}
        >
          <Stack direction={"column"} gap={{ base: 8, md: 10 }} mb={16}>
            <BulletPoint>{t("ToWhom.benefits1")}</BulletPoint>
            <BulletPoint>{t("ToWhom.benefits2")}</BulletPoint>
            <BulletPoint>{t("ToWhom.benefits3")}</BulletPoint>
            <BulletPoint>{t("ToWhom.benefits4")}</BulletPoint>
            <BulletPoint>{t("ToWhom.benefits5")}</BulletPoint>
            <BulletPoint>{t("ToWhom.benefits6")}</BulletPoint>
          </Stack>
          <Center w="full">
            <Image src={happyWoman} alt="Happy Woman" />
          </Center>
        </Grid>
        <DashDivider />
        <Box>
          <Heading fontSize={"3xl"} fontWeight={600} textAlign={"center"}>
            Dashboard
          </Heading>
          <Text mb={8} textAlign={"center"}>
            {t("ToWhom.myDashboardDescription")}
          </Text>
          <Center w="full">
            <Box
              w="full"
              maxWidth={{
                base: "full",
                md: "700px",
              }}
              border="8px solid"
              borderColor="black.900"
              position="relative"
            >
              <Image src={coachDashboardImage} alt="Dashboard" quality={80} />
            </Box>
          </Center>
        </Box>
        <DashDivider />
        <Box>
          <Heading fontSize={"3xl"} fontWeight={600} textAlign={"center"}>
            {t("ToWhom.salesPipeline")}
          </Heading>
          <Text mb={8} textAlign={"center"}>
            {t("ToWhom.salesPipelineDescription")}
          </Text>
          <Center w="full">
            <Box
              w="full"
              maxWidth={{
                base: "full",
                md: "700px",
              }}
              border="8px solid"
              borderColor="black.900"
              position="relative"
            >
              <Image
                src={salesPipelineImage}
                alt="Sales Pipeline"
                quality={80}
              />
            </Box>
          </Center>
        </Box>
        <DashDivider />
        <Box>
          <Heading fontSize={"3xl"} fontWeight={600} textAlign={"center"}>
            {t("ToWhom.financialPlanner")}
          </Heading>
          <Text mb={8} textAlign={"center"}>
            {t("ToWhom.financialPlannerDescription")}
          </Text>
          <Center w="full">
            <Box
              w="full"
              maxWidth={{
                base: "full",
                md: "700px",
              }}
              border="8px solid"
              borderColor="black.900"
              position="relative"
            >
              <Image
                src={financialPlannerImage}
                alt="Financial Planner"
                quality={80}
              />
            </Box>
          </Center>
        </Box>
        <DashDivider />
        <BirdNestIcon width={60} height={60} />
        <Link href="/pricing">
          <Button
            size="lg"
            fontSize="2xl"
            w={{
              base: "full",
              md: "auto",
            }}
            variant={"primary"}
          >
            {t("Pricing.wantClarifayo")}
          </Button>
        </Link>
      </VStack>
    </Container>
  );
};

const CoachTeamSection = () => {
  const t = useTranslations();
  return (
    <Container
      maxW="container.xl"
      py={{ base: 8, md: 16 }}
      px={{ base: 8, md: 24 }}
      id="master_coach"
    >
      <SectionHeader
        title={t("Pricing.master_coach")}
        description={t("ToWhom.coachTeamSection.description")}
      />

      <VStack
        w="full"
        gap={{
          base: 8,
          md: 16,
        }}
      >
        <Heading fontSize={"3xl"} fontWeight={600}>
          {t("ToWhom.benefits")}
        </Heading>
        <DashDivider />
        <Grid
          w="full"
          gridTemplateColumns={{
            base: "repeat(1, 1fr)",
            md: "repeat(2, 1fr)",
          }}
          gap={16}
        >
          <Stack
            direction={"column"}
            gap={{
              base: 8,
              md: 10,
            }}
            mb={16}
          >
            <BulletPoint>{t("ToWhom.benefits7")}</BulletPoint>
            <BulletPoint>{t("ToWhom.benefits8")}</BulletPoint>
            <BulletPoint>{t("ToWhom.benefits9")}</BulletPoint>
            <BulletPoint>{t("ToWhom.benefits10")}</BulletPoint>
            <BulletPoint>{t("ToWhom.benefits11")}</BulletPoint>
            <BulletPoint>{t("ToWhom.benefits12")}</BulletPoint>
            <BulletPoint>{t("ToWhom.benefits6")}</BulletPoint>
          </Stack>
          <Center w="full">
            <Image src={coachWithTeam} alt="Coach With Team" />
          </Center>
        </Grid>
        <DashDivider />

        <Box>
          <Heading fontSize={"3xl"} fontWeight={600} textAlign={"center"}>
            Dashboard
          </Heading>
          <Text mb={8} textAlign={"center"}>
            {t("ToWhom.myDashboardDescription")}
          </Text>
          <Center w="full">
            <Box
              w="full"
              maxWidth={{
                base: "full",
                md: "700px",
              }}
              border="8px solid"
              borderColor="black.900"
              position="relative"
            >
              <Image
                src={coachWithTeamDashboardImage}
                alt="Dashboard"
                quality={80}
              />
            </Box>
          </Center>
        </Box>
        <DashDivider />
        <Box>
          <Heading fontSize={"3xl"} fontWeight={600} textAlign={"center"}>
            {t("ToWhom.teamDashboard")}
          </Heading>
          <Text mb={8} textAlign={"center"}>
            {t("ToWhom.teamDashboardDescription")}
          </Text>
          <Center w="full">
            <Box
              w="full"
              maxWidth={{
                base: "full",
                md: "700px",
              }}
              border="8px solid"
              borderColor="black.900"
              position="relative"
            >
              <Image
                src={teamDashboardImage}
                alt="Active clients"
                quality={80}
              />
            </Box>
          </Center>
        </Box>
        <DashDivider />
        <Box>
          <Heading fontSize={"3xl"} fontWeight={600} textAlign={"center"}>
            {t("ToWhom.teamPerformance")}
          </Heading>
          <Text mb={8} textAlign={"center"}>
            {t("ToWhom.teamPerformanceDescription")}
          </Text>
          <Center w="full">
            <Box
              w="full"
              maxWidth={{
                base: "full",
                md: "700px",
              }}
              border="8px solid"
              borderColor="black.900"
              position="relative"
            >
              <Image src={teamPerformanceImage} alt="Dashboard" quality={80} />
            </Box>
          </Center>
        </Box>
        <DashDivider />

        <BirdNestIcon width={60} height={60} />
        <Link href="/pricing">
          <Button
            size="lg"
            fontSize="2xl"
            w={{
              base: "full",
              md: "auto",
            }}
            variant={"primary"}
          >
            {t("Pricing.wantClarifayo")}
          </Button>
        </Link>
      </VStack>
    </Container>
  );
};

const EnterpriseSection = () => {
  const t = useTranslations();
  return (
    <Container
      maxW="container.xl"
      py={{ base: 8, md: 16 }}
      px={{ base: 8, md: 24 }}
      id="enterprise"
    >
      <SectionHeader
        title={t("roles.enterprise")}
        description={t("ToWhom.enterpriseSection.description")}
      />

      <VStack
        w="full"
        gap={{
          base: 8,
          md: 16,
        }}
      >
        <Heading fontSize={"3xl"} fontWeight={600}>
          {t("ToWhom.benefits")}
        </Heading>
        <DashDivider />
        <Grid
          w="full"
          gridTemplateColumns={{
            base: "repeat(1, 1fr)",
            md: "repeat(2, 1fr)",
          }}
          gap={16}
        >
          <Stack
            direction={"column"}
            gap={{
              base: 8,
              md: 10,
            }}
            mb={16}
          >
            <BulletPoint>{t("ToWhom.benefits13")}</BulletPoint>
            <BulletPoint>{t("ToWhom.benefits14")}</BulletPoint>
            <BulletPoint>{t("ToWhom.benefits15")}</BulletPoint>
            <BulletPoint>{t("ToWhom.benefits16")}</BulletPoint>
            <BulletPoint>{t("ToWhom.benefits17")}</BulletPoint>
            <BulletPoint>{t("ToWhom.benefits18")}</BulletPoint>
          </Stack>
          <Center w="full">
            <Image src={enterprise} alt="Enterprise" />
          </Center>
        </Grid>

        <Box>
          <Heading fontSize={"3xl"} fontWeight={600} textAlign={"center"}>
            Dashboard
          </Heading>
          <Text mb={8} textAlign={"center"}>
            {t("ToWhom.enterpriseDashboardDescription")}
          </Text>
          <Center w="full">
            <Box
              w="full"
              maxWidth={{
                base: "full",
                md: "700px",
              }}
              border="8px solid"
              borderColor="black.900"
              position="relative"
            >
              <Image
                src={coachWithTeamDashboardImage}
                alt="Dashboard"
                quality={80}
              />
            </Box>
          </Center>
        </Box>
        <DashDivider />
        <Box>
          <Heading fontSize={"3xl"} fontWeight={600} textAlign={"center"}>
            {t("ToWhom.customForms")}
          </Heading>
          <Text mb={8} textAlign={"center"}>
            {t("ToWhom.customFormsDescription")}
          </Text>
          <Center w="full">
            <Box
              w="full"
              maxWidth={{
                base: "full",
                md: "700px",
              }}
              border="8px solid"
              borderColor="black.900"
              position="relative"
            >
              <Image src={customFormsImage} alt="Active clients" quality={80} />
            </Box>
          </Center>
        </Box>
        <DashDivider />
        <Box>
          <Heading fontSize={"3xl"} fontWeight={600} textAlign={"center"}>
            {t("ToWhom.communicationTools")}
          </Heading>
          <Text mb={8} textAlign={"center"}>
            {t("ToWhom.communicationToolsDescription")}
          </Text>
          <Center w="full">
            <Box
              w="full"
              maxWidth={{
                base: "full",
                md: "700px",
              }}
              border="8px solid"
              borderColor="black.900"
              position="relative"
            >
              <Image src={teamPerformanceImage} alt="Dashboard" quality={80} />
            </Box>
          </Center>
        </Box>
        <DashDivider />
        <BirdNestIcon width={60} height={60} />
        <Link href="/pricing">
          <Button
            size="lg"
            fontSize="2xl"
            w={{
              base: "full",
              md: "auto",
            }}
            variant={"primary"}
          >
            {t("Pricing.wantClarifayo")}
          </Button>
        </Link>
      </VStack>
    </Container>
  );
};

export default function ToWhom() {
  const router = useRouter();
  const scrollTo = (id: string) => {
    const element = document.getElementById(id);
    element?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (router.query.scrollTo) {
      scrollTo(router.query.scrollTo as string);
    }
  }, [router.query.scrollTo]);

  return (
    <>
      <Head>
        <title>Clarifayo - maximize your Coaching Activity</title>
      </Head>
      <Box w="full" bgColor="brand.100">
        <Hero />
        <Container mt={8} maxW="container.xl">
          <RoleToggler clickCallback={scrollTo} />
        </Container>
        <CoachSection />
        <CoachTeamSection />
        <EnterpriseSection />
        <Spacer my={16} />
        <TestimonialsSection />
        <GetStarted />
        <Footer />
      </Box>
    </>
  );
}

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {},
  };
};
